/**
 * RecentChangesTimeline — Compact inline audit trail showing recent changes
 * to a specific settings area. Drop into any settings page to show who
 * changed what and when.
 */

import { useState } from 'react';
import { Box, Typography, Stack, Skeleton, Chip, Collapse, IconButton } from '@mui/material';
import { History as HistoryIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { auditApi, AuditLogEntry } from '@/api/endpoints/audit';
import { TYPE } from '@/theme/typography';

interface RecentChangesTimelineProps {
  /** Table name(s) to filter audit entries by (e.g. 'companies', 'users') */
  tableName?: string | string[];
  /** Max entries to show */
  limit?: number;
  /** Title override */
  title?: string;
}

/** Human-friendly action labels */
function formatAction(action: string): { label: string; color: 'success' | 'info' | 'warning' | 'error' | 'default' } {
  switch (action.toLowerCase()) {
    case 'create': return { label: 'Created', color: 'success' };
    case 'update': return { label: 'Updated', color: 'info' };
    case 'delete': return { label: 'Deleted', color: 'error' };
    case 'void': return { label: 'Voided', color: 'warning' };
    default: return { label: action, color: 'default' };
  }
}

/** Format timestamp to relative or absolute */
function formatTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Summarize what changed */
function summarizeChanges(entry: AuditLogEntry): string {
  if (!entry.new_values || typeof entry.new_values !== 'object') {
    return entry.table_name.replace(/_/g, ' ');
  }
  const keys = Object.keys(entry.new_values).filter(
    (k) => !['updated_at', 'created_at', 'id', 'company_id', 'group_id'].includes(k),
  );
  if (keys.length === 0) return entry.table_name.replace(/_/g, ' ');
  if (keys.length <= 3) return keys.map((k) => k.replace(/_/g, ' ')).join(', ');
  return `${keys.slice(0, 2).map((k) => k.replace(/_/g, ' ')).join(', ')} +${keys.length - 2} more`;
}

export default function RecentChangesTimeline({
  tableName,
  limit = 5,
  title = 'Recent Changes',
}: RecentChangesTimelineProps) {
  const tables = Array.isArray(tableName) ? tableName : tableName ? [tableName] : undefined;

  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['audit-timeline', tables, limit],
    queryFn: () =>
      auditApi.list({
        table_name: tables?.[0],
        per_page: limit,
        page: 1,
      }),
    select: (r) => r.data?.items ?? [],
    staleTime: 2 * 60 * 1000,
  });

  // Filter client-side for multiple tables
  const entries = tables && tables.length > 1
    ? (data ?? []).filter((e) => tables.includes(e.table_name))
    : (data ?? []);

  if (isLoading) {
    return (
      <Box sx={{ mt: 3 }}>
        <Skeleton variant="text" width={120} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rounded" height={40} sx={{ mt: 1 }} />
        ))}
      </Box>
    );
  }

  if (entries.length === 0) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={0.75}
        onClick={() => setOpen((v) => !v)}
        sx={{
          cursor: 'pointer',
          py: 0.75,
          px: 1,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
          transition: 'background 0.15s',
        }}
      >
        <HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" color="text.secondary" fontWeight={TYPE.semibold} sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Chip label={entries.length} size="small" sx={{ height: 20, fontSize: TYPE.xs, bgcolor: 'action.selected' }} />
        <IconButton size="small" aria-label={open ? 'Collapse recent changes' : 'Expand recent changes'} sx={{ p: 0.25 }}>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.25s ease',
            }}
          />
        </IconButton>
      </Stack>

      <Collapse in={open} timeout={250}>
        <Stack spacing={0} sx={{ mt: 0.5 }}>
          {entries.map((entry, idx) => {
            const { label, color } = formatAction(entry.action);
            return (
              <Stack
                key={entry.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  py: 1,
                  px: 1.5,
                  borderRadius: 1,
                  borderBottom: idx < entries.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                  transition: 'background 0.12s',
                }}
              >
                <Chip
                  label={label}
                  color={color}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 70, fontSize: TYPE.xs }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={TYPE.medium} noWrap>
                    {summarizeChanges(entry)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
                  {entry.user_name || entry.user_email || 'System'}
                </Typography>
                <Typography variant="caption" color="text.disabled" noWrap sx={{ flexShrink: 0, minWidth: 60, textAlign: 'right' }}>
                  {formatTime(entry.timestamp)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </Collapse>
    </Box>
  );
}
